import { computed } from "vue";
import { useStore } from "vuex";
import router from "@/router";
import Billing from "@/services/billing/billing";
import { GET_SUBSCRIPTION_STATUS } from "@/store/action-types";
import { useSubscriptionModals } from "@/composables/ui/useSubscriptionModals";

export const USAGE_BLOCKED_TOOLTIP = "Subscription inactive — update billing to continue";

export function useBillingStatus() {
  const store = useStore();

  const isTrialing = computed(() => store.getters.isTrialing);
  const hasPaymentMethod = computed(() => store.getters.hasPaymentMethod);
  const trialEndsAt = computed(() => store.getters.trialEndsAt);
  const daysLeftInTrial = computed(() => store.getters.daysLeftInTrial);
  const subscriptionStatus = computed(() => store.getters.subscriptionStatus?.data);
  const isLoadingStatus = computed(() => store.getters.subscriptionStatus?.isLoading ?? false);
  const isInternalUser = computed(() => store.getters.isInternalUser);

  const fetchSubscriptionStatus = async (forceRefresh = false) => {
    return await store.dispatch(GET_SUBSCRIPTION_STATUS, { forceRefresh });
  };

  const { showUsageBasedFeaturesModal, hideUsageBasedFeaturesModal, showInsufficientCreditsModal } =
    useSubscriptionModals();

  const ALLOWED_USAGE_STATUSES = new Set(["active", "trialing"]);

  const MINIMUM_BALANCE = 200; // $2.00, matches MINIMUM_CALL_BALANCE_CENTS in backend

  const getCreditBalance = async (): Promise<number> => {
    try {
      const result = await Billing.getCreditBalance();
      return result?.data?.data?.balanceCents ?? 0;
    } catch {
      return 0;
    }
  };

  const getCreditBalanceWithRetry = async (): Promise<number> => {
    let balance = await getCreditBalance();
    if (balance < MINIMUM_BALANCE) {
      const justPurchasedAt = sessionStorage.getItem("sb_credits_just_purchased");
      if (justPurchasedAt && Date.now() - Number(justPurchasedAt) < 60_000) {
        await new Promise<void>((resolve) => setTimeout(resolve, 2000));
        balance = await getCreditBalance();
        if (balance >= MINIMUM_BALANCE) {
          sessionStorage.removeItem("sb_credits_just_purchased");
        }
      }
    }
    return balance;
  };

  /**
   * Runs callback immediately if credit balance is sufficient, otherwise shows the
   * usage-based features modal with the appropriate CTA based on payment method status.
   */
  const withCredits = async (callback: () => void | Promise<void>): Promise<void> => {
    if (isInternalUser.value) {
      await callback();
      return;
    }

    await fetchSubscriptionStatus();

    // Block usage features when the subscription itself is inactive
    // (past_due / unpaid / canceled / incomplete). Credits are irrelevant in that case.
    const status = subscriptionStatus.value?.status as string | undefined;
    const hasSub = subscriptionStatus.value?.hasSubscription;
    if (hasSub && status && !ALLOWED_USAGE_STATUSES.has(status)) {
      showInsufficientCreditsModal(
        "Your subscription is inactive. Update your billing to continue using usage-based features.",
        "Subscription Inactive",
        "Update Billing",
        false,
        "/settings/billing"
      );
      router.push({ path: "/settings/billing" });
      return;
    }

    const balanceCents = await getCreditBalanceWithRetry();

    if (balanceCents >= MINIMUM_BALANCE) {
      await callback();
      return;
    }

    // No/low credits — has payment method → offer purchase
    if (hasPaymentMethod.value) {
      showUsageBasedFeaturesModal({
        hasPaymentMethod: true,
        balanceCents,
        onPurchaseCredits: () => {
          hideUsageBasedFeaturesModal();
          router.push({ path: "/settings/billing", query: { openPurchase: "true" } });
        },
        onAddPaymentMethod: null,
        onProceed: null,
      });
      return;
    }

    // No payment method → set up billing first
    showUsageBasedFeaturesModal({
      hasPaymentMethod: false,
      balanceCents,
      onAddPaymentMethod: null, // modal handles checkout directly
      onProceed: null,
      onPurchaseCredits: null,
    });
  };

  /**
   * Checks credit balance — proceeds directly if sufficient, otherwise shows
   * the usage-based features modal with the appropriate CTA.
   * Kept as a named wrapper so the 12+ call sites don't need changes.
   */
  const showUsageBasedFeaturesModalOnce = async (
    _featureKey: string,
    callback: () => void | Promise<void>
  ): Promise<void> => {
    await withCredits(callback);
  };

  const usageBlockedState = computed(() => {
    const data = subscriptionStatus.value;
    const status = (data?.status ?? null) as string | null;
    const hasSub = !!data?.hasSubscription;
    if (!hasSub || !status) return { blocked: false, reason: null as string | null };
    if (!ALLOWED_USAGE_STATUSES.has(status)) {
      return { blocked: true, reason: status };
    }
    return { blocked: false, reason: null as string | null };
  });

  const isUsageBlocked = computed(() => usageBlockedState.value.blocked);

  return {
    isTrialing,
    hasPaymentMethod,
    trialEndsAt,
    daysLeftInTrial,
    subscriptionStatus,
    isLoadingStatus,
    fetchSubscriptionStatus,
    withCredits,
    showUsageBasedFeaturesModalOnce,
    usageBlockedState,
    isUsageBlocked,
  };
}
